import { type ThemeOptions } from "@mui/material/styles";
import "@fontsource-variable/geist/wght.css";

export const overrideOptions: ThemeOptions = {
  // Have to override the default typography to use Geist custom font
  typography: {
    fontFamily: "Geist, Helvetica, Arial, sans-serif",
    h1: {
      fontFamily: "Geist, Helvetica, Arial, sans-serif",
    },
    h2: {
      fontFamily: "Geist, Helvetica, Arial, sans-serif",
    },
    h3: {
      fontFamily: "Geist, Helvetica, Arial, sans-serif",
    },
    h4: {
      fontFamily: "Geist, Helvetica, Arial, sans-serif",
    },
    h5: {
      fontFamily: "Geist, Helvetica, Arial, sans-serif",
    },
    h6: {
      fontFamily: "Geist, Helvetica, Arial, sans-serif",
    },
    body1: {
      fontFamily: "Geist, Helvetica, Arial, sans-serif",
    },
    body2: {
      fontFamily: "Geist, Helvetica, Arial, sans-serif",
    },
    subtitle1: {
      fontFamily: "Geist, Helvetica, Arial, sans-serif",
    },
    subtitle2: {
      fontFamily: "Geist, Helvetica, Arial, sans-serif",
    },
    overline: {
      fontFamily: "Geist, Helvetica, Arial, sans-serif",
    },
    caption: {
      fontFamily: "Geist, Helvetica, Arial, sans-serif",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ":root": {
          "--mdai-background-default": "#150D17",
          "--mdai-background-top": "#2B1631",
          "--mdai-background-bottom": "#150D17",
          "--mdai-background-header":
            "linear-gradient(180deg, #150D17 79.97%, rgba(21, 13, 23, 0.00) 100.2%)",
          "--mdai-background-footer":
            "linear-gradient(180deg,rgba(21, 13, 23, 0.00) 0%, rgba(21, 13, 23, 1) 10%)",
          "--mdai-purple": "#8E24AA",
          "--mdai-vibrant-purple": "#D800FC",
          "--mdai-light-purple": "#d865f2",
          "--mdai-dark-purple": "#65406D",
          "--mdai-dark-purple-2": "#29192D",
          "--mdai-soft-purple": "#ddb7e6",
          "--mdai-deep-purple": "#D1C4E9",
          "--mdai-darkest-purple": "#311F35",
          "--mdai-branded-purple": "#6D3B78",
          "--mdai-light-grey": "#ededed",
          "--mdai-dark-grey": "#323232",
          "--mui-palette-primary-_states-outlinedBorder": "#D800FC",
          "--mdai-divider": "rgba(255, 255, 255, 0.12)",
        },
        body: {
          fontFamily: "Geist, Helvetica, Arial, sans-serif",
        },
        a: {
          color: "var(--mdai-vibrant-purple)",
        },
      },
    },
    MuiButton: {
      defaultProps: {
        size: "medium",
        variant: "contained",
      },
      styleOverrides: {
        root: {
          textTransform: "none",
          fontFamily: "Geist, Helvetica, Arial, sans-serif !important",
          borderRadius: "4px",
          fontWeight: 500,
          "&& .MuiTouchRipple-root": {
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0, // full coverage
            width: "100%",
            height: "100%",
            background: "transparent",
            opacity: 1, // let ripple animation control opacity
            borderRadius: "inherit",
          },
          "&&:not(.Mui-disabled):not(.Mui-focusVisible):not(:hover) .MuiTouchRipple-root":
            {
              width: "100%",
              height: "100%",
              background: "transparent",
              opacity: 1,
            },
          "&&.Mui-focusVisible:not(:hover) .MuiTouchRipple-root": {
            width: "100%",
            height: "100%",
            background: "transparent",
            opacity: 1,
          },
        },
        contained: {
          color: "#FFFFFF",
          width: "fit-content",
          background: "var(--mdai-purple)",
        },
        outlined: {
          borderWidth: "2px",
          color: "var(--mdai-vibrant-purple) !important",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: "none",
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          padding: "24px",
          height: "84px",
          boxSizing: "border-box",
          ["@media (min-width: 600px)"]: {
            minHeight: "initial",
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: "12px",
          padding: "20px 28px 20px 18px",
          maxWidth: "unset",
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        list: {
          padding: 0,
        },
        paper: {
          background: "var(--mdai-background-bottom)",
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          background: "var(--mdai-background-bottom)",
          "&.Mui-expanded": {
            margin: 0,
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        expandIconWrapper: {
          color: "inherit",
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: "#3A3A3A",
          position: "relative",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        filled: () => ({
          backgroundColor: "rgba(255, 255, 255, 0.16)",
        }),
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: "none",
          color: "var(--mdai-vibrant-purple)",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: "var(--mdai-background-bottom)",
          boxShadow: "none",
          borderBottom: "none",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: "#AAAAAA",
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          backgroundColor: "rgb(255 255 255 / 15%)",
          "&:hover": {
            backgroundColor: "rgb(255 255 255 / 20%)",
          },
          "&.Mui-focused": {
            backgroundColor: "rgb(255 255 255 / 20%)",
          },
        },
        underline: {
          "&:before": {
            borderBottom: "2px solid rgba(255, 255, 255, 0.42)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: "#242424",
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          color: "#AAAAAA",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: "#AAA",
        },
      },
    },
  },
};
